/// <amd-module name="ServiceHooks/Web/Admin/Components/ServiceHookDetailView/ServiceHookDetailView" />
import * as React from "react";
import { SubscriptionDiagnostics } from "ServiceHooks/Web/Admin/Contracts/Notifications";
import * as Resources from "ServiceHooks/Web/Admin/Resources";
import { IReadonlyObservableValue, ObservableValue, useObservable, useObservableArray } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { Ago } from "VSSUI/Ago";
import { Card } from "VSSUI/Card";
import { Header, TitleSize } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Link } from "VSSUI/Link";
import { Observer } from "VSSUI/Observer";
import { renderLoadingCell, TableColumnLayout } from "VSSUI/Table";
import { AgoFormat } from "VSSUI/Utilities/Date";
import { Filter, IFilterState } from "VSSUI/Utilities/Filter";
import { VssPersona } from "VSSUI/VssPersona";
import { ZeroData } from "VSSUI/ZeroData";
import { ServiceHooksDataContext } from "../../Context/ServiceHooksDataContext";
import { Permissions } from "../../Contracts/Permissions";
import { Notification, Subscription, SubscriptionStatus } from "../../Contracts/ServiceHooks";
import { HistoryFilterBar } from "../HistoryFilterBar/HistoryFilterBar";
import { HistoryTable } from "../HistoryTable/HistoryTable";
import { HistoryTableFilterIds } from "../HistoryTable/HistoryTable.Props";
import { IServiceHookDetailCardProps, IServiceHookDetailViewProps } from "./ServiceHookDetailView.Props";

export const ServiceHookDetailView: React.FunctionComponent<IServiceHookDetailViewProps> = props => {
    const {
        onBackClick,
        onEditSubscription,
        onEnableDiagnostics,
        onHistoryRowActivated,
        onDeleteSubscription,
        onQueryDiagnostics,
        onQueryNotifications,
        onUpdateSubscriptionStatus,
        subscription,
        userPermissions,
        zeroDataImagePath
    } = props;
    const dataContext = React.useContext(ServiceHooksDataContext);

    const [filter] = React.useState(() => {
        const defaultState: IFilterState = {};
        defaultState[HistoryTableFilterIds.DAYSBACK] = { value: [1] };
        return new Filter({ defaultState: defaultState });
    });

    const [notifications, setNotifications] = useObservableArray<Notification | IReadonlyObservableValue<Notification | undefined>>([]);
    // Gross, but the only way I could find to check if this value has changed or not when the filter updates
    // Using actual state will trigger a re-render, which is worse
    const lastDaysBackValue = React.useRef<Number>(1);

    const [diagnostics, setDiagnostics] = useObservable<SubscriptionDiagnostics | undefined>(undefined);

    React.useEffect(() => {
        setNotifications(new Array(5).fill(new ObservableValue(undefined)));

        // Always start on 24h
        onQueryNotifications(subscription, 1).then(result => {
            setNotifications(result);
        });

        onQueryDiagnostics(subscription).then(result => {
            setDiagnostics(result);
        });

        const handleFilterChange = () => {
            const filterDaysBack = filter.getFilterItemValue<number[]>(HistoryTableFilterIds.DAYSBACK);

            if (filterDaysBack && filterDaysBack[0] && filterDaysBack[0] != lastDaysBackValue.current) {
                lastDaysBackValue.current = filterDaysBack[0];
                // Loading behavior in the interim
                setNotifications(new Array(5).fill(new ObservableValue(undefined)));

                onQueryNotifications(subscription, filterDaysBack[0]).then(result => {
                    setNotifications(result);
                });
            }
        };

        filter.subscribe(handleFilterChange);

        return () => filter.unsubscribe(handleFilterChange);
    }, []);

    const consumerId = subscription.consumerId;
    const actionId = subscription.consumerActionId;
    const consumerName = dataContext.consumersById[consumerId] ? dataContext.consumersById[consumerId].name : Resources.Unknown;
    const actionName = dataContext.consumerActionsById[consumerId + actionId]
        ? dataContext.consumerActionsById[consumerId + actionId].name
        : Resources.Unknown;

    const getHeaderCommandBarItems = () => {
        const items: IHeaderCommandBarItem[] = [];
        if (userPermissions[Permissions.EditSubscriptions]) {
            items.push(
                { id: "edit", text: Resources.TableMenuEdit, iconProps: { iconName: "Edit" }, onActivate: () => onEditSubscription(subscription) },
                {
                    id: "enableDisable",
                    text: subscription.status === SubscriptionStatus.Enabled ? Resources.TableMenuDisable : Resources.TableMenuEnable,
                    iconProps: { iconName: subscription.status === SubscriptionStatus.Enabled ? "CirclePause" : "Play" },
                    onActivate:
                        subscription.status === SubscriptionStatus.Enabled
                            ? () => onUpdateSubscriptionStatus(SubscriptionStatus.DisabledByUser, subscription)
                            : () => onUpdateSubscriptionStatus(SubscriptionStatus.Enabled, subscription)
                },
                {
                    id: "delete",
                    text: Resources.TableMenuDelete,
                    iconProps: { iconName: "Delete" },
                    onActivate: () => onDeleteSubscription(subscription)
                }
            );
        }
        return items;
    };

    const onEnableDiagnosticsWrapper = () => {
        onEnableDiagnostics(subscription).then(results => {
            setDiagnostics(results);
        });
    };

    return (
        <React.Fragment>
            <Header
                backButtonProps={{ onClick: onBackClick }}
                commandBarItems={getHeaderCommandBarItems()}
                description={`${actionName} ${subscription.actionDescription}`}
                title={`${consumerName}`}
                titleSize={TitleSize.Large}
            />

            <div className="page-content page-content-top rhythm-vertical-8">
                <SubscriptionDetailCard diagnostics={diagnostics} onEnableDiagnostics={onEnableDiagnosticsWrapper} subscription={subscription} />

                <HistoryFilterBar filter={filter} />

                <Observer contextDataLoaded={dataContext.dataLoaded}>
                    {(observerProps: { contextDataLoaded: boolean }) => {
                        return (
                            observerProps.contextDataLoaded && (
                                <React.Fragment>
                                    <Observer notifications={notifications}>
                                        {(observerProps: {
                                            notifications: (Notification | IReadonlyObservableValue<Notification | undefined>)[];
                                        }) => {
                                            return notifications.length > 0 ? (
                                                <HistoryTable filter={filter} notifications={notifications} onRowActivated={onHistoryRowActivated} />
                                            ) : (
                                                <ZeroData
                                                    imagePath={zeroDataImagePath}
                                                    imageAltText={Resources.ZeroDataPrimaryText}
                                                    primaryText={Resources.ZeroDataPrimaryText}
                                                    secondaryText={Resources.ZeroDataNotificationsSecondaryText}
                                                />
                                            );
                                        }}
                                    </Observer>
                                </React.Fragment>
                            )
                        );
                    }}
                </Observer>
            </div>
        </React.Fragment>
    );
};

export const SubscriptionDetailCard: React.FunctionComponent<IServiceHookDetailCardProps> = props => {
    const { diagnostics, onEnableDiagnostics, subscription } = props;
    const dataContext = React.useContext(ServiceHooksDataContext);

    const eventName = dataContext.eventTypesById[subscription.eventType]
        ? dataContext.eventTypesById[subscription.eventType].name
        : Resources.Unknown;

    return (
        <Card className="hooks-service-hook-detail-card" contentProps={{ className: "flex-row" }}>
            <div className="hooks-subscription-detail-group flex-column">
                <div className="title-xs">{Resources.BladeTriggerTitle}</div>
                <div className="body-m">
                    {eventName} - {subscription.eventDescription}
                </div>
            </div>

            <div className="hooks-subscription-detail-group flex-column">
                <div className="title-xs">{Resources.DataOwnerName}</div>
                <div className="flex-row flex-center">
                    {subscription.createdBy ? (
                        <>
                            <VssPersona
                                className="icon-margin"
                                displayName={subscription.createdBy.displayName}
                                imageUrl={subscription.createdBy.imageUrl}
                                size={"extra-small-plus"}
                            />
                            <div className="body-m">{subscription.createdBy.displayName}</div>
                        </>
                    ) : (
                        <div className="body-m">{Resources.Unknown}</div>
                    )}
                </div>
            </div>

            <div className="hooks-subscription-detail-group flex-column">
                <div className="title-xs">{Resources.DataModifiedAt}</div>
                <div className="flex-row flex-center">
                    {subscription.modifiedBy ? (
                        <>
                            <VssPersona
                                className="icon-margin"
                                displayName={subscription.modifiedBy.displayName}
                                imageUrl={subscription.modifiedBy.imageUrl}
                                size={"extra-small-plus"}
                            />
                            <div className="body-m">
                                {subscription.modifiedBy.displayName} (<Ago date={subscription.modifiedDate} format={AgoFormat.Compact} />)
                            </div>
                        </>
                    ) : (
                        <div className="body-m">
                            {Resources.Unknown} (<Ago date={subscription.modifiedDate} format={AgoFormat.Compact} />)
                        </div>
                    )}
                </div>
            </div>

            <div className="hooks-subscription-detail-group flex-column">
                <div className="title-xs">{Resources.DetailedLoggingName}</div>
                <Observer diagnostics={diagnostics}>
                    {(observerProps: { diagnostics: SubscriptionDiagnostics | undefined }) => {
                        return !observerProps.diagnostics ? (
                            renderLoadingCell(TableColumnLayout.singleLinePrefix)
                        ) : (
                            <div className="body-m">{getDeliveryResultsString(observerProps.diagnostics, onEnableDiagnostics)}</div>
                        );
                    }}
                </Observer>
            </div>
        </Card>
    );
};

const getDeliveryResultsString = (diagnostics: SubscriptionDiagnostics, onEnableDiagnostics: () => void) => {
    const deliveryResults = diagnostics.deliveryResults;

    if (!deliveryResults || (deliveryResults.enabled && deliveryResults.maxTracedEntries == 2147483647)) {
        return Resources.DetailedLoggingExpiresNever;
    } else if (deliveryResults.enabled) {
        return format(Resources.DetailedLoggingExpiresOn, deliveryResults.endDate.toLocaleDateString());
    } else {
        return <Link onClick={onEnableDiagnostics}>{Resources.DetailedLoggingExpired}</Link>;
    }
};
